import { useEffect, useState } from 'react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

/**
 * usePromo — fetches the current automatic-promo state from the backend.
 *
 * The same helper (utils.promo.get_promo_state) drives the real Stripe
 * checkout amount, so the UI can never show a discount that is not actually
 * applied to the card transaction. Once the window ends, `active` flips to
 * false and the banner disappears with no deploy.
 *
 * Returns:
 *   {
 *     loading: boolean,
 *     active: boolean,
 *     discount_pct: number,        // e.g. 25 (percent)
 *     ends_at_iso: string,
 *     label_sk: string,
 *     individual: { base_eur, final_eur, currency },
 *     school:     { base_eur, final_eur, currency },
 *   }
 */
export default function usePromo() {
  const [state, setState] = useState({
    loading: true,
    active: false,
    discount_pct: 0,
    ends_at_iso: '',
    label_sk: '',
    individual: { base_eur: 14.99, final_eur: 14.99, currency: 'eur' },
    school: { base_eur: 250.0, final_eur: 250.0, currency: 'eur' },
  })

  useEffect(() => {
    let cancelled = false
    fetch(`${BACKEND_URL}/api/promo/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return
        setState({ ...data, loading: false })
      })
      .catch(() => {
        if (cancelled) return
        // On network failure: never show a discount we can't confirm.
        setState((s) => ({ ...s, loading: false, active: false }))
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}

/**
 * Format a number as a Slovak EUR amount with comma decimal: "14,99 €".
 */
export function formatEur(amount) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return ''
  return `${amount.toFixed(2).replace('.', ',')} €`
}
